import { memo, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { Heading } from '@library';
import styles from './CollapsibleContent.module.scss';
import HorizontalLayout from './layout/HorizontalLayout';
import ChevronRightIcon from './icons/ChevronRightIcon';

export interface CollapsibleContentProps {
    expanded?: boolean;
    onExpand?: (show: boolean) => void;
    title: ReactNode;
    children: ReactNode;
    persistentChildren?: ReactNode;
    filterIcon?: boolean;
    labelVisible?: boolean;
    smallIcon?: boolean;
    endArrow?: boolean;
    headerContent?: ReactNode;
    reversedTriggerContent?: boolean;
    iconMode?: 'rightDown' | 'downUp';
    hoverEffects?: boolean;
    keepPersistentChildrenOnExpand?: boolean;
}

const CollapsibleContent = ({
    expanded,
    onExpand,
    title,
    children,

    persistentChildren,
    endArrow = false,
    filterIcon = false,
    labelVisible = false,
    headerContent = false,
    smallIcon,
    reversedTriggerContent = false,
    hoverEffects = true,
    keepPersistentChildrenOnExpand = false,
    iconMode = 'rightDown',
    ...ariaOrDataProps
}: CollapsibleContentProps) => {
    const [isExpanded, setIsExpanded] = useState(expanded);

    useEffect(() => {
        if (expanded !== undefined) {
            setIsExpanded(expanded);
        }
    }, [expanded]);

    const triggerExpand = () => {
        setIsExpanded(!isExpanded);
        if (onExpand) {
            onExpand(!isExpanded);
        }
    };

    return (
        <div
            {...ariaOrDataProps}
            className={clsx(styles.collapsibleContentContainer, {
                [styles.expanded]: isExpanded,
                [styles.hoverEffects]: hoverEffects,
            })}
        >
            <div
                role="button"
                tabIndex={0}
                className={styles.collapsibleContentTrigger}
                onClick={() => {
                    triggerExpand();
                }}
                onKeyPress={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        triggerExpand();
                    }
                }}
            >
                <HorizontalLayout
                    gap="small"
                    align="center"
                    justify={endArrow ? 'spaceBetween' : 'start'}
                    reversed={reversedTriggerContent}
                >
                    {!filterIcon && (
                        <div
                            className={clsx(styles.chevronIcon, {
                                [styles.chevronIconDownUp]:
                                    iconMode === 'downUp',
                            })}
                        >
                            <ChevronRightIcon
                                smallIcon={smallIcon}
                                color={hoverEffects ? undefined : '#000'}
                            />
                        </div>
                    )}
                    <Heading h6 medium uppercase>
                        <div className={styles.collapsibleContentTitle}>
                            {title}
                        </div>
                    </Heading>
                    {headerContent}
                    {filterIcon && (
                        <div className={styles.flex}>
                            {labelVisible && (
                                <div className={styles.label}>
                                    {isExpanded && (
                                        <FormattedMessage id="fade out" />
                                    )}
                                    {!isExpanded && (
                                        <FormattedMessage id="fade in" />
                                    )}
                                </div>
                            )}
                            <div className={styles.chevronFilterIcon}>
                                <ChevronRightIcon smallIcon color="#000" />
                            </div>
                        </div>
                    )}
                </HorizontalLayout>
                {(!isExpanded || keepPersistentChildrenOnExpand) &&
                    persistentChildren}
            </div>
            {isExpanded && (
                <div className={styles.collapsibleContent}>{children}</div>
            )}
        </div>
    );
};

export default memo(CollapsibleContent);
